import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";
import { downloadCsv, countsToRows } from "@/lib/exportCsv";

const COLORS = ["#2563eb", "#059669", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#65a30d"];
const AREAS = ["PTA", "JHB", "WTB", "CPT", "PE", "BFN", "DBN"];

const toPieData = (counts) => Object.entries(counts || {}).map(([name, value]) => ({ name, value }));

const ExportButton = ({ onClick, testId }) => (
  <Button variant="outline" size="sm" className="gap-2" onClick={onClick} data-testid={testId}>
    <Download className="h-4 w-4" />
    Export CSV
  </Button>
);

const CountList = ({ counts, emptyLabel, testId }) => {
  const entries = Object.entries(counts || {});
  if (!entries.length) return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  return (
    <div className="space-y-2 max-h-80 overflow-y-auto" data-testid={testId}>
      {entries.map(([key, value]) => {
        const isDetailed = value && typeof value === "object";
        return (
          <div key={key} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
            <span className="truncate pr-3">{key}</span>
            <span className="font-semibold whitespace-nowrap">
              {isDetailed ? `${value.count} (${value.percent}%)` : value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default function PipelineReportsInsightsPage() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [area, setArea] = useState("all");
  const [pipelineType, setPipelineType] = useState("SS");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const fetchReports = useCallback(async () => {
    setLoading(true);
    const params = {
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
      ...(area !== "all" && { area }),
    };
    const pipelineParams = { ...params, pipeline_type: pipelineType };

    try {
      const [tracking, areas, cancellations, leadTimes, delays, venues] = await Promise.all([
        axios.get(`${API}/reports/referral-tracking`, { params, withCredentials: true }),
        axios.get(`${API}/reports/area-concentration`, { params, withCredentials: true }),
        axios.get(`${API}/reports/cancellations`, { params: pipelineParams, withCredentials: true }),
        axios.get(`${API}/reports/contact-lead-time`, { params: pipelineParams, withCredentials: true }),
        axios.get(`${API}/reports/delay-reasons`, { params: pipelineParams, withCredentials: true }),
        axios.get(`${API}/reports/facilitation-venue`, { params: pipelineParams, withCredentials: true }),
      ]);
      setData({
        tracking: tracking.data,
        areas: areas.data,
        cancellations: cancellations.data,
        leadTimes: leadTimes.data,
        delays: delays.data,
        venues: venues.data,
      });
    } catch (error) {
      console.error("Pipeline reports load failed", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, area, pipelineType]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const leadTimeRows = Object.entries(data.leadTimes || {}).map(([contact, stats]) => ({
    contact: contact.replace("contact_", "Contact "),
    ...stats,
  }));

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pipeline-reports-page">
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-2xl font-bold tracking-tight">Pipeline Reports</h1>
        <p className="text-sm text-muted-foreground">Referrals, areas, cancellations, lead times, delays and venues</p>
      </div>

      <div className="bg-white border-y px-6 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">Date from</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" data-testid="report-date-from" />
          </div>
          <div>
            <Label className="text-xs">Date to</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" data-testid="report-date-to" />
          </div>
          <div>
            <Label className="text-xs">Area</Label>
            <Select value={area} onValueChange={setArea}>
              <SelectTrigger className="mt-1" data-testid="report-area-select">
                <SelectValue placeholder="All areas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All areas</SelectItem>
                {AREAS.map((a) => (
                  <SelectItem key={a} value={a}>
                    {a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Pipeline</Label>
            <Select value={pipelineType} onValueChange={setPipelineType}>
              <SelectTrigger className="mt-1" data-testid="report-pipeline-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SS">Sleep Study (SS)</SelectItem>
                <SelectItem value="TT">Titration (TT)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={fetchReports} disabled={loading} className="w-full" data-testid="report-apply-btn">
            {loading ? "Loading..." : "Apply filters"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24" data-testid="reports-loading">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="px-6 py-6">
          <Tabs defaultValue="referral">
            <TabsList className="flex flex-wrap h-auto" data-testid="report-tabs">
              <TabsTrigger value="referral" data-testid="tab-referral">Referrals</TabsTrigger>
              <TabsTrigger value="area" data-testid="tab-area">Areas</TabsTrigger>
              <TabsTrigger value="cancellation" data-testid="tab-cancellation">Cancellations</TabsTrigger>
              <TabsTrigger value="leadtime" data-testid="tab-leadtime">Lead times</TabsTrigger>
              <TabsTrigger value="delay" data-testid="tab-delay">Delay reasons</TabsTrigger>
              <TabsTrigger value="venue" data-testid="tab-venue">Venues</TabsTrigger>
            </TabsList>

            <TabsContent value="referral" className="mt-4">
              <Card data-testid="referral-tracking-report">
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                  <div>
                    <CardTitle className="text-base">Referral tracking</CardTitle>
                    <CardDescription>
                      {data.tracking?.total_referrals ?? 0} referrals in the selected period
                    </CardDescription>
                  </div>
                  <ExportButton
                    testId="export-referral-tracking"
                    onClick={() =>
                      downloadCsv("referral-tracking", ["Group", "Key", "Referrals"], [
                        ...countsToRows(data.tracking?.by_rep).map(([k, v]) => ["Rep", k, v]),
                        ...countsToRows(data.tracking?.by_doctor).map(([k, v]) => ["Doctor", k, v]),
                      ])
                    }
                  />
                </CardHeader>
                <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-semibold mb-3">By rep</h3>
                    <CountList counts={data.tracking?.by_rep} emptyLabel="No referrals in this period" testId="tracking-by-rep" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold mb-3">By referring doctor</h3>
                    <CountList counts={data.tracking?.by_doctor} emptyLabel="No referrals in this period" testId="tracking-by-doctor" />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="area" className="mt-4">
              <Card data-testid="area-concentration-report">
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                  <div>
                    <CardTitle className="text-base">Area concentration</CardTitle>
                    <CardDescription>Where referrals are coming from</CardDescription>
                  </div>
                  <ExportButton
                    testId="export-area-concentration"
                    onClick={() =>
                      downloadCsv("area-concentration", ["Level", "Name", "Referrals"], [
                        ...countsToRows(data.areas?.by_area).map(([k, v]) => ["Area", k, v]),
                        ...countsToRows(data.areas?.by_suburb).map(([k, v]) => ["Suburb", k, v]),
                      ])
                    }
                  />
                </CardHeader>
                <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-72">
                    {toPieData(data.areas?.by_area).length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={toPieData(data.areas?.by_area)}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={95}
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {toPieData(data.areas?.by_area).map((entry, index) => (
                              <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-muted-foreground">No referrals in this period</p>
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold mb-3">By suburb</h3>
                    <CountList counts={data.areas?.by_suburb} emptyLabel="No suburb data captured" testId="area-by-suburb" />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cancellation" className="mt-4">
              <Card data-testid="cancellations-report">
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                  <div>
                    <CardTitle className="text-base">Cancellations</CardTitle>
                    <CardDescription>
                      {data.cancellations?.total_cancellations ?? 0} lost cases in {pipelineType}
                    </CardDescription>
                  </div>
                  <ExportButton
                    testId="export-cancellations"
                    onClick={() =>
                      downloadCsv("cancellations", ["Group", "Key", "Count", "Percent"], [
                        ...countsToRows(data.cancellations?.by_stage_lost).map(([k, c, p]) => ["Stage", k, c, p]),
                        ...countsToRows(data.cancellations?.by_reason).map(([k, c, p]) => ["Reason", k, c, p]),
                        ...countsToRows(data.cancellations?.by_rep).map(([k, c, p]) => ["Rep", k, c, p]),
                      ])
                    }
                  />
                </CardHeader>
                <CardContent className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <h3 className="text-sm font-semibold mb-3">By stage lost</h3>
                    <CountList counts={data.cancellations?.by_stage_lost} emptyLabel="No cancellations" testId="cancel-by-stage" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold mb-3">By reason</h3>
                    <CountList counts={data.cancellations?.by_reason} emptyLabel="No cancellations" testId="cancel-by-reason" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold mb-3">By rep</h3>
                    <CountList counts={data.cancellations?.by_rep} emptyLabel="No cancellations" testId="cancel-by-rep" />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="leadtime" className="mt-4">
              <Card data-testid="lead-time-report">
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                  <div>
                    <CardTitle className="text-base">Contact lead times</CardTitle>
                    <CardDescription>Days taken to reach the patient per contact attempt</CardDescription>
                  </div>
                  <ExportButton
                    testId="export-lead-times"
                    onClick={() =>
                      downloadCsv(
                        "contact-lead-times",
                        ["Contact", "Average days", "Min", "Max", "Cases"],
                        leadTimeRows.map((row) => [row.contact, row.average_days, row.min, row.max, row.count])
                      )
                    }
                  />
                </CardHeader>
                <CardContent>
                  {leadTimeRows.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No lead times captured yet for {pipelineType}.</p>
                  ) : (
                    <>
                      <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={leadTimeRows}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="contact" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="average_days" name="Average days" fill="#2563eb" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {leadTimeRows.map((row) => (
                          <div key={row.contact} className="bg-gray-50 rounded-lg p-3" data-testid={`lead-time-${row.contact.replace(/\s+/g, "-").toLowerCase()}`}>
                            <p className="text-sm font-semibold mb-2">{row.contact}</p>
                            <div className="grid grid-cols-4 gap-2 text-xs">
                              <div>
                                <p className="text-muted-foreground">Avg</p>
                                <p className="font-semibold">{row.average_days}d</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Min</p>
                                <p className="font-semibold">{row.min}d</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Max</p>
                                <p className="font-semibold">{row.max}d</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Cases</p>
                                <p className="font-semibold">{row.count}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="delay" className="mt-4">
              <Card data-testid="delay-reasons-report">
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                  <div>
                    <CardTitle className="text-base">Delay reasons</CardTitle>
                    <CardDescription>Why contact attempts did not turn into an appointment</CardDescription>
                  </div>
                  <ExportButton
                    testId="export-delay-reasons"
                    onClick={() =>
                      downloadCsv(
                        "delay-reasons",
                        ["Contact", "Reason", "Count"],
                        Object.entries(data.delays || {}).flatMap(([contact, reasons]) =>
                          Object.entries(reasons).map(([reason, count]) => [contact.replace("contact_", "Contact "), reason, count])
                        )
                      )
                    }
                  />
                </CardHeader>
                <CardContent>
                  {Object.keys(data.delays || {}).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No delay reasons captured yet for {pipelineType}.</p>
                  ) : (
                    <div className="space-y-5">
                      {Object.entries(data.delays).map(([contact, reasons]) => (
                        <div key={contact} data-testid={`delay-${contact}`}>
                          <h3 className="text-sm font-semibold mb-2">{contact.replace("contact_", "Contact ")}</h3>
                          <CountList counts={reasons} emptyLabel="None" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="venue" className="mt-4">
              <Card data-testid="venue-report">
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                  <div>
                    <CardTitle className="text-base">Facilitation venues</CardTitle>
                    <CardDescription>Where {pipelineType} facilitations take place</CardDescription>
                  </div>
                  <ExportButton
                    testId="export-venues"
                    onClick={() => downloadCsv("facilitation-venues", ["Venue", "Facilitations"], countsToRows(data.venues))}
                  />
                </CardHeader>
                <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-72">
                    {toPieData(data.venues).length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={toPieData(data.venues)}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={95}
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {toPieData(data.venues).map((entry, index) => (
                              <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-muted-foreground">No facilitation venues captured yet.</p>
                    )}
                  </div>
                  <CountList counts={data.venues} emptyLabel="No facilitation venues captured yet." testId="venue-list" />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
